import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import AddLocation from "@mui/icons-material/AddLocation";
import FactoryIcon from "@mui/icons-material/Factory";
import OwnerIcon from "@mui/icons-material/PersonSearch";
import AddressIcon from "@mui/icons-material/LocationCity";
import ScaleIcon from "@mui/icons-material/Scale";
import CoordinatesIcon from "@mui/icons-material/MyLocation";
import FormField from "../../../components/FormField";
import ILocation from "../../../types/ILocation";
import UOMFilter from "../../../components/filters/UomFilter";
import ZipCodeFilter from "../../../components/filters/ZipCodeFilter";
import NitroService from "../../../utils/nitro.service";
import CityFilter from "../../../components/filters/CityFilter";
import CompanyFilter from "../../../components/filters/CompanyFilter";
import { useNitroStore } from "../../../store/Store";
import NumberTextfield from "../../../components/NumberTextfield";
import { useIsComponentMounted } from "../../../hooks/useIsComponentMounted";
import RailSettings from "../../../components/logistics/components/RailSettings";
import { useIsMobile } from "../../../hooks/useIsMobile";
import LogisticsRow from "../../../components/logistics/components/LogistcsRow";
import { RailroadIcon } from "../../../icons";
import { IMode } from "../../../types/IModes";
import { toModes } from "../../../utils/utils";

const FacilityForm = ({
  disabled,
  originalLocation,
  location,
  setLocation,
  startSelectingCoordinates,
}: {
  disabled?: boolean;
  originalLocation?: ILocation;
  location: ILocation;
  setLocation: Dispatch<SetStateAction<ILocation | undefined>>;
  startSelectingCoordinates?: () => void;
}) => {
  const isMobile = useIsMobile(768);

  const [setPanToCoordinates, setMapType] = useNitroStore((state) => [
    state.setPanToCoordinates,
    state.setMapType,
  ]);
  const modes = toModes(location.inboundModes, location.outboundModes);

  const [zipCodes, setZipCodes] = useState<string[]>([]);

  const parseCityState = (cityState: string) => {
    const parts = cityState.split(",").map((part) => part.trim());
    return {
      city: parts[0] || "",
      state: parts[1] || "",
    };
  };

  const getZipCodeDetails = useCallback(
    async (zipCode: string) => {
      const records = await NitroService.getZipCodeDetails(zipCode);
      if (!records || records.length === 0) {
        return;
      }

      setMapType("hybrid");

      const { state, country, coordinates } = records[0];

      setLocation((prev) => ({
        ...(prev ?? location),
        // Preserve parsed City/State when ZipCodeDetails omits fields so required validation stays true.
        state: state || (prev ? prev.state : location.state),
        country: country || (prev ? prev.country : location.country) || "USA",
        zipCode,
        coordinates: [coordinates.y, coordinates.x],
        stationState: "",
        carriers: [],
      }));

      if (!isMobile) {
        setPanToCoordinates({
          lat: coordinates.y,
          lng: coordinates.x,
          zoom: 14,
        });
      }

      if (startSelectingCoordinates) startSelectingCoordinates();
    },
    [
      isMobile,
      location,
      setLocation,
      setMapType,
      setPanToCoordinates,
      startSelectingCoordinates,
    ],
  );

  const isComponentMounted = useIsComponentMounted();

  useEffect(() => {
    if (!isComponentMounted) return;
    if (!location.city) {
      console.info("Clearing city info - no city");
      setLocation((prev) => ({
        ...(prev ?? location),
        country: "USA",
        city: "",
        state: "",
        zipCode: "",
        coordinates: ["", ""],
      }));
      setZipCodes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.city]);

  const [addLocationDetails] = useNitroStore((state) => [
    state.addLocationDetails,
  ]);
  useEffect(() => {
    if (addLocationDetails) {
      setLocation((prev) => ({
        ...(prev ?? location),
        zipCode: addLocationDetails.zipCode,
        city: addLocationDetails.cityState,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLocationDetails, setLocation]);

  useEffect(() => {
    document.getElementById("facilityNameTextbox")?.focus();
  }, [disabled]);

  if (disabled) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          fontWeight: 300,
          color: "#595959",
          border: "1px solid lightgray",
          borderRadius: 4,
          padding: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <FactoryIcon style={{ color: "#005CA6" }} /> {location.name}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <OwnerIcon style={{ color: "#005CA6" }} />
          {location.owner && <div>{location.owner}</div>}
          {!location.owner && (
            <div style={{ fontStyle: "italic" }}>
              No facility owner assigned
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <AddressIcon style={{ color: "#005CA6" }} />
          {`${location.address || ""} ${location.city}, ${location.state} ${
            location.zipCode
          }`}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <CoordinatesIcon style={{ color: "#005CA6" }} />
          {`${location.coordinates[0]}, ${location.coordinates[1]}`}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ScaleIcon style={{ color: "#005CA6" }} />
          {location.uom || "Unknown"}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <FormField label="Facility name*" width="100%">
        <TextField
          id="facilityNameTextbox"
          disabled={disabled}
          placeholder="Facility name"
          size="small"
          fullWidth
          value={location.name}
          onChange={(e) =>
            setLocation({
              ...location,
              name: e.target.value,
            })
          }
        />
      </FormField>
      <FormField
        label="Facility owner"
        width="100%"
        tooltip="Facility owners who have been added in Salesforce"
      >
        <CompanyFilter
          disabled={disabled}
          value={location.owner}
          setValue={(value) =>
            setLocation({
              ...location,
              owner: value,
            })
          }
          isMobile={isMobile}
        />
      </FormField>
      <FormField label="Address" width="100%">
        <TextField
          disabled={disabled}
          placeholder="Address"
          size="small"
          fullWidth
          value={location.address}
          onChange={(e) =>
            setLocation({
              ...location,
              address: e.target.value,
            })
          }
        />
      </FormField>

      <div style={{ display: "flex" }}>
        <FormField label="City, State*" width="55%">
          <CityFilter
            disabled={disabled}
            isMobile={isMobile}
            value={location.city}
            setValue={(value: any) => {
              const parsedCityState = parseCityState(value || "");
              setLocation((prev) => ({
                ...(prev ?? location),
                city: parsedCityState.city,
                state: parsedCityState.state,
                zipCode: "",
                country: "",
                coordinates: ["", ""],
              }));
            }}
            setZipCodes={setZipCodes}
          />
        </FormField>

        <div
          style={{
            display: "flex",
            gap: 10,
            paddingLeft: 10,
            width: "calc(45% - 10px)",
          }}
        >
          <FormField
            label="Zip*"
            width="60%"
            disabled={disabled || !location.city}
          >
            <ZipCodeFilter
              zipCodes={zipCodes}
              disabled={disabled || !location.city}
              value={location.zipCode}
              setValue={(value) => {
                if (value) {
                  getZipCodeDetails(value);
                }
              }}
            />
          </FormField>

          <div style={{ width: "calc(40% - 10px)" }}>
            <FormField label="Country*">
              <TextField
                placeholder="Country"
                size="small"
                value={location.country}
                disabled
              />
            </FormField>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
        }}
      >
        <FormField
          label="Coordinates*"
          disabled={!location.zipCode}
          width="55%"
        >
          <div style={{ display: "flex", gap: 5 }}>
            <NumberTextfield
              placeholder="Latitude"
              size="small"
              fullWidth
              error={
                Number(location.coordinates[0]) > 90 ||
                Number(location.coordinates[0]) < -90
              }
              value={location.coordinates[0] as any}
              setValue={(val) => {
                setLocation({
                  ...location,
                  coordinates: [val, location.coordinates[1]],
                });
              }}
              disabled={disabled || !location.zipCode}
            />
            <NumberTextfield
              placeholder="Longitude"
              size="small"
              fullWidth
              error={
                Number(location.coordinates[1]) > 180 ||
                Number(location.coordinates[1]) < -180
              }
              value={location.coordinates[1] as number}
              setValue={(val) => {
                setLocation({
                  ...location,
                  coordinates: [location.coordinates[0], val],
                });
              }}
              disabled={disabled || !location.zipCode}
            />
          </div>
        </FormField>
        <div style={{ paddingLeft: 10, flexGrow: 1 }}>
          {!disabled && (
            <Button
              id="changeCoordinatesButton"
              disabled={!location.zipCode}
              style={{
                width: isMobile ? "100%" : "60%",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10,
                paddingLeft: 0,
                paddingRight: 0,
                height: 32,
              }}
              size="small"
              variant="contained"
              onClick={() => {
                setMapType("hybrid");
                if (startSelectingCoordinates) {
                  startSelectingCoordinates();
                }
              }}
            >
              <AddLocation
                style={{
                  fontSize: 18,
                }}
              />
              <div>Change coordinates</div>
            </Button>
          )}
        </div>
      </div>

      <FormField label="UOM*" width="55%">
        <UOMFilter
          disabled={disabled}
          value={location.uom}
          setValue={(value) =>
            setLocation({
              ...location,
              uom: value,
            })
          }
        />
      </FormField>

      {(originalLocation?.inboundModes.includes("RAIL") ||
        originalLocation?.outboundModes.includes("RAIL")) &&
        originalLocation &&
        originalLocation?.zipCode !== location.zipCode && (
          <div
            style={{
              border: "3px solid #148dd3",
              padding: 15,
              borderRadius: 10,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{ fontStyle: "italic", fontSize: 12, color: "#797979" }}
            >
              This facility is setup with a rail station and carriers. Updating
              the zip code likely changes the rail station and carrier options.
              Validate these below.
            </div>

            <LogisticsRow
              label="Rail"
              icon={<RailroadIcon color="#09B0F2" />}
              mode={modes.RAIL}
              updateMode={(mode: IMode) => {
                if (!mode.inbound && !mode.outbound) {
                  setLocation({
                    ...location,
                    stationState: "",
                    equipmentTypes: [],
                    carriers: [],
                    inboundModes: location.inboundModes.filter(
                      (m) => m !== "RAIL",
                    ),
                    outboundModes: location.outboundModes.filter(
                      (m) => m !== "RAIL",
                    ),
                  });
                } else {
                  const updatedLocation = {
                    ...location,
                  };
                  if (mode.inbound && !location.inboundModes.includes("RAIL")) {
                    updatedLocation.inboundModes = [
                      ...location.inboundModes,
                      "RAIL",
                    ];
                  } else if (!mode.inbound) {
                    updatedLocation.inboundModes = location.inboundModes.filter(
                      (m) => m !== "RAIL",
                    );
                  }

                  setLocation(updatedLocation);
                }
              }}
              disabled={disabled}
            />
            {(location.inboundModes.includes("RAIL") ||
              location.outboundModes.includes("RAIL")) && (
              <RailSettings
                location={location}
                setLocation={setLocation}
                disabled={disabled}
                zipCode={location.zipCode}
                originalLocation={originalLocation}
              />
            )}
          </div>
        )}
    </div>
  );
};

export default FacilityForm;
